import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import BookingManagement from "@/components/BookingManagement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Users, Clock, TrendingUp } from "lucide-react";

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showAllBookings, setShowAllBookings] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [matchesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/matches?status=upcoming`),
        axios.get(`${API}/my-bookings`)
      ]);
      setMatches(matchesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { label: "Aktif Maçlar", value: matches.length, icon: Users, color: "text-emerald-600" },
    { label: "Rezervasyonlar", value: bookings.length, icon: Calendar, color: "text-blue-600" },
    { label: "Hız", value: user?.stats?.speed || 50, icon: TrendingUp, color: "text-orange-600" },
    { label: "Güç", value: user?.stats?.power || 50, icon: TrendingUp, color: "text-red-600" }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background transition-colors duration-300" data-testid="dashboard">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8 relative overflow-hidden rounded-3xl text-white p-8 md:p-12 shadow-xl" style={{ background: 'linear-gradient(to right, rgba(147, 112, 219, 0.55), rgba(147, 112, 219, 0.8))' }}>
          <div className="absolute inset-0 opacity-10">
            <div className="field-pattern h-full w-full"></div>
          </div>
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Hoş Geldin, {user?.name}!</h1>
            <p className="text-lg md:text-xl text-purple-100 mb-6">Maçlarını takip et, sahalarını rezerve et, performansını artır.</p>
            <Button 
              onClick={() => navigate("/venues")} 
              className="bg-white hover:bg-purple-50"
              style={{ color: '#9370DB' }}
              data-testid="browse-venues-btn"
            >
              Sahaları Keşfet
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, index) => (
            <Card key={index} className="hover-lift cursor-pointer" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                  <span className="text-3xl font-bold">{stat.value}</span>
                </div>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Upcoming Matches */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-emerald-600" />
                Yaklaşan Maçlar
              </CardTitle>
              <CardDescription>Katılabileceğin maçlar</CardDescription>
            </CardHeader>
            <CardContent>
              {matches.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz yaklaşan maç yok</p>
              ) : (
                <div className="space-y-4">
                  {matches.slice(0, 3).map((match) => (
                    <div 
                      key={match.id} 
                      className="p-4 border rounded-lg hover:border-emerald-500 hover:shadow-md transition-all cursor-pointer"
                      onClick={() => navigate(`/matches/${match.id}`)}
                      data-testid={`match-card-${match.id}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold text-lg">{match.venueName}</h3>
                        <Badge className="bg-emerald-100 text-emerald-700">
                          {match.players.length}/{match.maxPlayers}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {match.date} - {match.time}
                        </div>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Kaptan: {match.captainName}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {matches.length > 3 && (
                <Button variant="outline" className="w-full mt-4" onClick={() => navigate("/venues")}>
                  Tümünü Gör
                </Button>
              )}
            </CardContent>
          </Card>

          {/* My Bookings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Rezervasyonlarım
              </CardTitle>
              <CardDescription>Aktif rezervasyonların</CardDescription>
            </CardHeader>
            <CardContent>
              {bookings.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Henüz rezervasyon yok</p>
              ) : (
                <div className="space-y-4">
                  {(showAllBookings ? bookings : bookings.slice(0, 3)).map((booking) => (
                    <div 
                      key={booking.id} 
                      className="p-4 border rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                      data-testid={`booking-card-${booking.id}`}
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowBookingDialog(true);
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-semibold">{booking.venueName}</h3>
                        <Badge 
                          variant={booking.status === 'confirmed' ? 'default' : 'outline'}
                          className={
                            booking.status === 'confirmed' ? 'bg-green-500' :
                            booking.status === 'pending' ? 'bg-yellow-500' :
                            booking.status === 'cancelled' ? 'bg-red-500' : ''
                          }
                        >
                          {booking.status === 'confirmed' ? 'Onaylandı' :
                           booking.status === 'pending' ? 'Beklemede' :
                           booking.status === 'cancelled' ? 'İptal' : booking.status}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {new Date(booking.date).toLocaleDateString('tr-TR')} - {booking.startTime}
                        </div>
                        <div className="flex items-center gap-2 font-semibold text-emerald-600">
                          <Clock className="h-4 w-4" />
                          {booking.duration} dakika - {parseFloat(booking.finalPrice).toFixed(2)} TL
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {bookings.length > 3 && (
                <Button 
                  variant="outline" 
                  className="w-full mt-4" 
                  onClick={() => setShowAllBookings(!showAllBookings)}
                >
                  {showAllBookings ? 'Daha Az Göster' : `Daha Fazla Gör (${bookings.length - 3})`}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Player Card Preview */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-6">
            <h2 className="text-2xl font-bold text-white mb-2">Oyuncu Kartın</h2>
            <p className="text-emerald-50">İstatistiklerini görüntüle ve geliştir</p>
          </div>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Hız</span>
                    <span className="text-emerald-600 font-bold">{user?.stats?.speed || 50}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-500 to-green-600 stat-bar" 
                      style={{ width: `${user?.stats?.speed || 50}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Güç</span>
                    <span className="text-red-600 font-bold">{user?.stats?.power || 50}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-red-500 to-orange-600 stat-bar" 
                      style={{ width: `${user?.stats?.power || 50}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Dayanıklılık</span>
                    <span className="text-blue-600 font-bold">{user?.stats?.stamina || 50}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-cyan-600 stat-bar" 
                      style={{ width: `${user?.stats?.stamina || 50}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Teknik</span>
                    <span className="text-purple-600 font-bold">{user?.stats?.technique || 50}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-600 stat-bar" 
                      style={{ width: `${user?.stats?.technique || 50}%` }}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium">Defans</span>
                    <span className="text-gray-600 font-bold">{user?.stats?.defense || 50}</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-gray-500 to-slate-600 stat-bar" 
                      style={{ width: `${user?.stats?.defense || 50}%` }}
                    ></div>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate(`/profile/${user.id}`)} 
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
                  data-testid="view-profile-btn"
                >
                  Profili Görüntüle
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Booking Management Dialog */}
      <BookingManagement
        booking={selectedBooking}
        isOpen={showBookingDialog}
        onClose={() => {
          setShowBookingDialog(false);
          setSelectedBooking(null);
        }}
        onUpdate={fetchData}
        user={user}
      />
    </div>
  );
};

export default Dashboard;